/**
 * Full-width banner shown while Auth on File RPA is running, with auth-status selection.
 * @module rpaStatusBanner
 */
import { TagOption, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { BadgeWithIconProps } from "../../../../data-model/badgeWithIconProps";
import { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { useAuthStatusDropdown } from "../../../../hooks/useAuthStatusDropdown";
import { InfoIcon } from "../../../../svg/info-icon";
import { getRpaStatusBanner } from "../../utils/bannerConfig";

/**
 * Renders the in-progress RPA message and auth-status dropdown when the banner should show.
 *
 * @returns Banner UI or null when {@link getRpaStatusBanner} indicates the banner is hidden
 */
export const RpaStatusBanner: React.FC = () => {
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const bannerConfig = getRpaStatusBanner(
    generalOrderDetails?.order ?? ({} as GeneralOrderDetails),
  );
  const { selectedAuthStatusBadge, handleAuthStatusChange, authStatusOptions } =
    useAuthStatusDropdown();

  if (!bannerConfig.show) {
    return null;
  }

  /**
   * Maps badge options into Oasis tag option objects.
   *
   * @param badges - Available badge options for the dropdown
   * @returns Oasis tag options for the Tags component
   */
  const toTagOptions = (badges: BadgeWithIconProps[]): TagOption[] => {
    return badges.map((badge) => ({
      label: badge.text,
      value: badge.id,
      backgroundColor: badge.bgColor,
      textColor: badge.textColor,
    }));
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4">
      <div className="flex flex-col items-center justify-center gap-2">
        <InfoIcon height="36" width="36" stroke="#0056d6" strokeWidth="1.4" />
        <div className="text-center">
          Please wait while we retrive the Auth on file run status.
          <br />
          You may revisit this page after a few minutes for the updated status.
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-sm font-normal text-primaryGray-1">
          Select Auth Status
        </div>
        <Tags
          key={`${selectedAuthStatusBadge?.id ?? ""}-${selectedAuthStatusBadge?.text ?? ""}-${Math.random()}`}
          label={selectedAuthStatusBadge?.text ?? ""}
          backgroundColor={selectedAuthStatusBadge?.bgColor ?? "#F5F5F5"}
          textColor={selectedAuthStatusBadge?.textColor ?? "#0F0F0F"}
          options={toTagOptions(authStatusOptions as BadgeWithIconProps[])}
          shouldUsePortal={true}
          onSelect={(option) => handleAuthStatusChange(option.value as string)}
        />
      </div>
    </div>
  );
};
